"use client";

import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { niceYTicks } from "@/lib/chart-ticks";
import type { SimResult } from "@/lib/simulator/types";

/* ──────────────────────────────────────────────────────────────────────────
 * ExplainerChart — the inline chart for /learn Act 1.
 *
 * Same visual DNA as the home page SimulatorChart (NK brand for portfolio,
 * NK accent for loan, hairline grid, year ticks), with one addition: a
 * shaded area between the loan line and the portfolio line labeled "Your
 * wealth" inside the chart. The shading is the visual point of the page —
 * the gap between the two lines IS the wealth being created.
 *
 * Renders inline on stone-50 with no card wrapper, per the /learn editorial
 * design. Wider aspect ratio than the home chart so the labels can sit
 * inside the plot without crowding the data.
 * ────────────────────────────────────────────────────────────────────────── */

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(value);

const chartConfig = {
  portfolioValue: {
    label: "Portfolio value",
    color: "var(--color-nk-brand)",
  },
  loanBalance: {
    label: "Loan balance",
    color: "var(--color-nk-accent)",
  },
} satisfies ChartConfig;

function isoToYearFraction(isoDate: string): number {
  const [yearStr, monthStr, dayStr] = isoDate.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const dayOfYear = (month - 1) * 30.44 + day;
  return year + dayOfYear / 365.25;
}

export function ExplainerChart({ result }: { result: SimResult }) {
  const stride = result.series.length > 500 ? Math.ceil(result.series.length / 500) : 1;
  const data = result.series
    .filter((_, idx) => idx === 0 || idx === result.series.length - 1 || idx % stride === 0)
    .map((step) => ({
      yearFraction: isoToYearFraction(step.date),
      date: step.date,
      portfolioValue: Math.round(step.portfolioValue),
      loanBalance: Math.round(step.loanBalance),
      // Tuple [loanBalance, portfolioValue] drives the shaded area between the lines.
      wealthBand: [Math.round(step.loanBalance), Math.round(step.portfolioValue)] as [
        number,
        number,
      ],
    }));

  const firstYear = Math.floor(data[0]!.yearFraction);
  const lastYear = Math.ceil(data[data.length - 1]!.yearFraction);
  const yearTicks: number[] = [];
  for (let y = firstYear; y <= lastYear; y++) yearTicks.push(y);

  // Round-number Y-axis ticks so the gridlines land on $5k / $10k / $25k
  // boundaries instead of recharts' default padded floor.
  const yMin = Math.min(...data.map((d) => d.loanBalance));
  const yMax = Math.max(...data.map((d) => d.portfolioValue));
  const yTicks = niceYTicks(yMin, yMax);

  return (
    <ChartContainer config={chartConfig} className="aspect-[16/9] w-full md:aspect-[16/8]">
      <ComposedChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--color-nk-border)" strokeDasharray="3 3" />
        <XAxis
          dataKey="yearFraction"
          type="number"
          domain={[firstYear, lastYear]}
          ticks={yearTicks}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => String(Math.round(v))}
          tickMargin={8}
          stroke="var(--color-nk-foreground-muted)"
          style={{ fontSize: 11 }}
        />
        <YAxis
          ticks={yTicks}
          domain={[yTicks[0]!, yTicks[yTicks.length - 1]!]}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => formatCurrency(v)}
          tickMargin={8}
          stroke="var(--color-nk-foreground-muted)"
          style={{ fontSize: 11 }}
          width={72}
        />
        <ChartTooltip
          cursor={{ stroke: "var(--color-nk-border-strong)", strokeWidth: 1 }}
          content={
            <ChartTooltipContent
              labelKey="date"
              labelFormatter={(
                label: unknown,
                payload: readonly { payload?: { date?: string } }[],
              ) => {
                const date = payload?.[0]?.payload?.date;
                if (!date) return String(label);
                return new Date(date).toLocaleDateString("en-CA", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });
              }}
              labelClassName="font-display"
              formatter={(value, name) => {
                // The wealth band entry is meta — hide it from the tooltip.
                if (name === "wealthBand") return null;
                return (
                  <div className="flex w-full items-center justify-between gap-4">
                    <span className="text-nk-foreground-muted">
                      {name === "portfolioValue" ? "Portfolio value" : "Loan balance"}
                    </span>
                    <span className="font-medium tabular-nums">
                      {formatCurrency(Number(value))}
                    </span>
                  </div>
                );
              }}
            />
          }
        />
        {/* Shaded wealth band between the two lines. */}
        <Area
          type="monotone"
          dataKey="wealthBand"
          stroke="none"
          fill="var(--color-nk-brand)"
          fillOpacity={0.1}
          isAnimationActive={false}
          activeDot={false}
        />
        <Line
          type="monotone"
          dataKey="portfolioValue"
          stroke="var(--color-nk-brand)"
          strokeWidth={1.75}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          type="monotone"
          dataKey="loanBalance"
          stroke="var(--color-nk-accent)"
          strokeWidth={1.75}
          strokeDasharray="6 4"
          dot={false}
          activeDot={{ r: 4 }}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
